import { useEffect } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';

import { ConsumerPosOrdersDocument } from 'src/gql/graphql';
import type { ConsumerPosOrdersQueryVariables, PosOrderFieldsFragment } from 'src/gql/graphql';
import { getInstoreOrderAddress } from 'shared/helpers/orders';
import { getLogger } from 'src/utils/datadog-logger';
import { ConsumerOrder } from '../../../account.types';

const logger = getLogger('use-instore-order-list');

type UseInstoreOrderListParams = {
  dispensaryId: string;
  skip?: boolean;
};

export type UseInstoreOrderListReturn = {
  orders: ConsumerOrder[];
  rawOrders: PosOrderFieldsFragment[];
  loading: boolean;
  hasMore: boolean;
  hasError: boolean;
  handleLoadMore: () => void;
  refetch: () => void;
};

const PAGE_LIMIT = 5;
const EMPTY_ORDERS: PosOrderFieldsFragment[] = [];

function posOrderToConsumerOrder(posOrder: PosOrderFieldsFragment): ConsumerOrder {
  const { id, grandTotal, shipmentDeliveredLocal, items, locationDetails } = posOrder;
  const address = getInstoreOrderAddress(locationDetails);

  return {
    orderId: id,
    totalCost: grandTotal.toString(),
    createdAt: shipmentDeliveredLocal,
    status: null,
    orders: (items ?? []).map((item) => ({
      quantity: item.quantity,
      productName: item.productName,
    })),
    dispensary: { address },
  };
}

export function useInstoreOrderList({
  dispensaryId,
  skip = false,
}: UseInstoreOrderListParams): UseInstoreOrderListReturn {
  const variables: ConsumerPosOrdersQueryVariables = {
    dispensaryId,
    pagination: { limit: PAGE_LIMIT, offset: 0 },
  };

  const { data, loading, error, networkStatus, fetchMore, refetch } = useQuery(ConsumerPosOrdersDocument, {
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (error) {
      logger.error('Failed to fetch in-store order history', { dispensaryId, errorMessage: error.message });
    }
  }, [error, dispensaryId]);

  const isLoading = loading || networkStatus === NetworkStatus.fetchMore;
  const posOrders = data?.consumerPosOrders?.orders ?? EMPTY_ORDERS;
  const totalCount = data?.consumerPosOrders?.meta?.totalCount ?? 0;

  const orders = posOrders.map(posOrderToConsumerOrder);

  const handleRefetch = (): void => {
    // Rejections surface via the Apollo error field and are logged in the useEffect above
    void refetch().catch(() => undefined);
  };

  const handleLoadMore = (): void => {
    void fetchMore({
      variables: {
        pagination: {
          limit: PAGE_LIMIT,
          offset: orders.length,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevOrders = prev.consumerPosOrders?.orders ?? [];
        const newOrders = fetchMoreResult.consumerPosOrders?.orders ?? [];

        return {
          ...fetchMoreResult,
          consumerPosOrders: {
            ...fetchMoreResult.consumerPosOrders,
            orders: [...prevOrders, ...newOrders],
          },
        };
      },
    }).catch((err: unknown) => {
      logger.error('Failed to load more in-store orders', {
        dispensaryId,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return {
    orders,
    rawOrders: posOrders,
    loading: isLoading,
    hasMore: orders.length < totalCount,
    hasError: !!error,
    handleLoadMore,
    refetch: handleRefetch,
  };
}
